import { definedMap } from "@glideapps/ts-necessities";

import {
    TableGlideType,
    ComponentKind,
    TableColumn,
    ComponentDescription,
    TitleDescription,
    LegacyPropertyDescription,
    TypeSchema,
    getTableName,
    InputOutputTables,
    TransformableContentDescription,
    getArrayTransforms,
    isSingleRelationType,
    findTable,
    MutatingScreenKind,
    getTableColumnDisplayName,
    makeEmptyComponentDescription,
    arrayScreenName,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    ComponentBuilder,
    ComponentDescriptor,
    ComponentErrorAndLink,
    componentErrorAndLinkForReference,
    SchemaInspector,
    InteractiveComponentConfiguratorContext,
    ComponentConfiguratorContext,
} from ".";
import { summaryColumnsForTable } from "../description-utils";
import { transformArray, withCaption } from "../builder-utils";
import { ComponentHandlerBase } from "./handler";
import {
    getColumnProperty,
    makeColumnProperty,
    makeStringProperty,
    makeTableProperty,
    getTableProperty,
} from "./property-utils";
import { makeCaptionStringPropertyDescriptor, makeTableOrRelationPropertyDescriptor } from "./descriptor-utils";
import {
    getListTypesAndContext,
    ListSourcePropertyDescription,
    getPropertyTable,
    getMultiRelationDescriptiveName,
    getListTypes,
} from "./inline-list";
import { propertyDescriptorsForTransforms } from "../array-screens/array-content";
import { getDocURL } from "../../docUrl";
import { PropertySection } from "./property-sections";

const ComponentKindListReference: ComponentKind = "list-reference";

interface ListReferenceComponentDescription
    extends ComponentDescription,
        TitleDescription,
        TransformableContentDescription {
    readonly propertyName: ListSourcePropertyDescription | undefined;
    readonly caption: LegacyPropertyDescription | undefined;
}

export class ListReferenceComponentHandler extends ComponentHandlerBase<ListReferenceComponentDescription> {
    constructor() {
        super(ComponentKindListReference);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ListReferenceComponentDescription,
        tables: InputOutputTables,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined,
        uniqueID: string
    ): SerializedComponent {
        const { b } = builder;

        const data = definedMap(desc.propertyName, pd => getListTypesAndContext(builder, pd, tables.input, false));
        if (data === undefined) return b.emptyUI;
        const { arrayType, itemsType, ctx } = data;

        if (!isSingleRelationType(itemsType)) return b.emptyUI;

        const itemsTable = builder.findTable(itemsType);
        if (itemsTable === undefined) return b.emptyUI;

        const arrayData = b.let(
            [["screen", b.getContext]],
            transformArray(
                builder,
                itemsTable,
                ctx,
                tables.input,
                b.getVariable("screen"),
                undefined,
                false,
                getArrayTransforms(desc),
                uniqueID
            )
        );

        const caption = this.getCaption(desc, tables.input, builder);
        const onTap = builder.makePushScreen(
            arrayType,
            definedMap(caption, b.constant) ?? b.localizedString("data"),
            undefined,
            b.getContext
        );

        return withCaption(builder, tables.input, desc.caption, [], c => {
            const child = b.functionCall("smartListItem", [c, onTap]);
            const ui = b.ifNonNull(b.getContext, child);
            return b.bindContextToValue(ui, arrayData);
        });
    }

    public getDescriptor(
        _desc: ListReferenceComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ListReferenceComponentDescription> {
        const getPT = (t: InputOutputTables, d: ListReferenceComponentDescription, s: SchemaInspector) =>
            getPropertyTable(t.input, d, s);
        return {
            name: "List Relation",
            description: "A link to a list of related items",
            img: "componentListRelation",
            group: "Lists",
            helpUrl: getDocURL("listReference"),
            properties: [
                makeTableOrRelationPropertyDescriptor(
                    mutatingScreenKind,
                    false,
                    true,
                    "Values",
                    PropertySection.Source,
                    true,
                    {
                        name: "propertyName",
                    }
                ),
                makeCaptionStringPropertyDescriptor("List", false, mutatingScreenKind),
                ...propertyDescriptorsForTransforms(getPT, true, true, true),
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    public getScreensUsed(
        desc: ListReferenceComponentDescription,
        schema: SchemaInspector,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        if (tables === undefined) return [];

        const data = definedMap(desc.propertyName, pd => getListTypes(schema, pd, tables.input, false));
        if (data === undefined || !isSingleRelationType(data.itemsType)) return [];

        return [arrayScreenName(data.itemsType)];
    }

    public static defaultComponent(
        schema: TypeSchema,
        column: TableColumn
    ): ListReferenceComponentDescription | undefined {
        if (column.type.kind !== "array") {
            return panic("Type of list reference column must be array");
        }

        const desc: ListReferenceComponentDescription = {
            ...makeEmptyComponentDescription(ComponentKindListReference),
            propertyName: makeColumnProperty(column.name),
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            transforms: [],
        };

        const maybeTableRef = column.type.items;
        if (isSingleRelationType(maybeTableRef)) {
            const summary = definedMap(findTable(schema, maybeTableRef), t =>
                summaryColumnsForTable(t, true, false, false)
            );
            if (summary === undefined) return undefined;
            Object.assign(desc, summary);
        }

        return desc;
    }

    // This is only here so it's possible to add this component without there being
    // a reference column.
    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ListReferenceComponentDescription | undefined {
        const desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc !== undefined) return desc;

        if (iccc.schema.tables.length > 0) {
            const table = iccc.schema.tables[0];
            return {
                ...makeEmptyComponentDescription(ComponentKindListReference),
                kind: ComponentKindListReference,
                propertyName: makeTableProperty(getTableName(table)),
                caption: "List relation",
                transforms: [],
            };
        }

        return {
            ...makeEmptyComponentDescription(ComponentKindListReference),
            propertyName: undefined,
            caption: "List relation",
            transforms: [],
        };
    }

    public getErrorAndLink(
        desc: ListReferenceComponentDescription,
        table: TableGlideType
    ): ComponentErrorAndLink | undefined {
        if (getColumnProperty(desc.propertyName) !== undefined || getTableProperty(desc.propertyName) !== undefined) {
            return undefined;
        }

        return componentErrorAndLinkForReference(table);
    }

    public getDescriptiveName(
        desc: ListReferenceComponentDescription,
        tables: InputOutputTables,
        ctx: ComponentConfiguratorContext
    ): [string, string] {
        const name = getMultiRelationDescriptiveName(desc, tables.input, ctx);

        if (name === undefined) {
            return super.getDescriptiveName(desc, tables, ctx);
        } else {
            return ["List relation", name];
        }
    }
}
